import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { ChangeEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import AddInIds from "@/constants/AddInIds";
import { useAddinData } from "@/hooks/addins/useAddinData";
import { useRemoveAddIn } from "@/hooks/addins/useRemoveAddIn";
import { useUpdateAddIn } from "@/hooks/addins/useUpdateAddIn";
import { DiagnosticView } from "@/models/DiagnosticView";

const TABLE_HEIGHT = 400;

export default function ViewBulkManager() {
  const { t } = useTranslation();
  const { data: views = [], isLoading } = useAddinData<DiagnosticView>(AddInIds.diagnosticViews);
  const updateAddIn = useUpdateAddIn<DiagnosticView>(AddInIds.diagnosticViews, "id");
  const removeAddIn = useRemoveAddIn<DiagnosticView>(AddInIds.diagnosticViews, "id");

  const [selected, setSelected] = useState<string[]>([]);
  const [importInProgress, setImportInProgress] = useState(false);

  const handleSelectAll = (e: ChangeEvent<HTMLInputElement>) => {
    setSelected(e.target.checked ? views.map(v => v.id) : []);
  };

  const handleToggle = (id: string) => {
    setSelected(prev => (prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]));
  };

  const handleExport = () => {
    const toExport = views.filter(v => selected.includes(v.id));
    const blob = new Blob([JSON.stringify(toExport, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "diagnostic-views.json";
    link.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportInProgress(true);
    try {
      const text = await file.text();
      const imported: DiagnosticView[] = JSON.parse(text);
      for (const v of imported) {
        if (views.find(existing => existing.id === v.id)) {
          alert(t("admin.viewManager.alreadyExists", { name: v.name }));
          continue;
        }
        await updateAddIn.mutateAsync(v);
      }
    } catch (err) {
      console.error(err);
      alert(t("admin.viewManager.importFailed"));
    } finally {
      setImportInProgress(false);
      e.target.value = "";
    }
  };

  const handleDelete = async () => {
    for (const id of selected) {
      await removeAddIn.mutateAsync({ delSearch: id });
    }
    setSelected([]);
  };

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <Button variant="contained" size="small" onClick={handleExport} disabled={selected.length === 0}>
          {t("admin.viewManager.export")}
        </Button>
        <Button variant="contained" size="small" component="label" loading={importInProgress}>
          {t("admin.viewManager.import")}
          <input type="file" accept="application/json" hidden onChange={handleImport} />
        </Button>
        <Button variant="contained" size="small" onClick={handleDelete} disabled={selected.length === 0}>
          {t("admin.viewManager.delete")}
        </Button>
      </Stack>

      <TableContainer component={Paper} variant="outlined" sx={{ height: TABLE_HEIGHT }}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < views.length}
                    checked={views.length > 0 && selected.length === views.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>{t("admin.viewManager.columnName")}</TableCell>
                <TableCell>{t("admin.viewManager.columnPages")}</TableCell>
                <TableCell>{t("admin.viewManager.columnGroups")}</TableCell>
                <TableCell>{t("admin.viewManager.columnColumnCount")}</TableCell>
                <TableCell>{t("admin.viewManager.columnUpdatedBy")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {views.map(v => {
                const isSel = selected.includes(v.id);
                return (
                  <TableRow key={v.id} hover onClick={() => handleToggle(v.id)} selected={isSel}>
                    <TableCell padding="checkbox">
                      <Checkbox checked={isSel} />
                    </TableCell>
                    <TableCell>{v.name}</TableCell>
                    <TableCell>
                      {v.pageTypes.map(p => (
                        <Chip key={p} size="small" label={p} sx={{ mr: 0.5 }} />
                      ))}
                    </TableCell>
                    <TableCell>
                      {v.groups.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          {t("admin.viewManager.allGroups")}
                        </Typography>
                      ) : (
                        v.groups.map(g => <Chip key={g.id} size="small" label={g.name ?? g.id} sx={{ mr: 0.5 }} />)
                      )}
                    </TableCell>
                    <TableCell>{v.columns.length}</TableCell>
                    <TableCell>{v.updatedBy}</TableCell>
                  </TableRow>
                );
              })}
              {views.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.viewManager.empty")}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
}
